import React from 'react';
import { SpeedInsights } from "@vercel/speed-insights/react"
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import PrivateRoute from './components/auth/PrivateRoute';
import Login from './pages/Login';
import CompanyRegistration from './pages/CompanyRegistration';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChangePassword from './pages/ChangePassword';
import Unauthorized from './pages/Unauthorized';
import SendAppreciation from './pages/SendAppreciation';
import MyAppreciations from './pages/MyAppreciations';
import Leaderboard from './pages/Leaderboard';
import Home from './pages/Home';
import AppreciationSuccess from './pages/AppreciationSuccess';
import MyBadges from './pages/MyBadges';
import CultureImpact from './pages/CultureImpact';
import Values from './pages/Values';
import Analytics from './pages/Analytics';
import AdminDashboard from './pages/AdminDashboard';
import AdminUserManagement from './pages/AdminUserManagement';
import AdminInvitations from './pages/AdminInvitations';
import AdminSettings from './pages/AdminSettings';
import AcceptInvitation from './pages/AcceptInvitation';
import Unsubscribe from './pages/Unsubscribe';
import AdminSubscriptions from './pages/AdminSubscriptions';
import SubscriptionCheckout from './pages/SubscriptionCheckout';
import OnboardingWizard from './components/onboarding/OnboardingWizard';
import { useAppContext } from './context/AppContext';
import './styles/tailwind.css';
import './styles/landing-page.css';

const RootRedirect = () => {
  const { currentUser, authChecked } = useAppContext();

  // Wait until auth check is complete
  if (!authChecked) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  // Redirect authenticated users to home, others to login
  return <Navigate to={currentUser ? "/home" : "/login"} replace />;
};

const AppRoutes = () => {
  const { isLoading } = useAppContext();

  // Show a loading screen if we're checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <svg className="animate-spin h-10 w-10 mx-auto mb-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">Loading Apprecio...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Smart root redirect based on authentication status */}
      <Route path="/" element={<RootRedirect />} />

      {/* Authenticated home page for logged-in users */}
      <Route path="/home" element={<PrivateRoute element={<Home />} />} />

      {/* Other public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<CompanyRegistration />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/change-password" element={<PrivateRoute element={<ChangePassword />} />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="/invitations/:token" element={<AcceptInvitation />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />

      {/* Onboarding wizard */}
      <Route path="/setup/onboarding" element={<PrivateRoute element={<OnboardingWizard />} />} />

      {/* Protected routes - any user */}
      <Route
        path="/send-appreciation"
        element={<PrivateRoute element={<SendAppreciation />} />}
      />
      <Route
        path="/my-appreciations"
        element={<PrivateRoute element={<MyAppreciations />} />}
      />
      <Route
        path="/leaderboard"
        element={<PrivateRoute element={<Leaderboard />} />}
      />
      <Route
        path="/my-badges"
        element={<PrivateRoute element={<MyBadges />} />}
      />
      <Route
        path="/culture-impact"
        element={<PrivateRoute element={<CultureImpact />} />}
      />
      <Route
        path="/values"
        element={<PrivateRoute element={<Values />} />}
      />
      <Route
        path="/appreciation-success"
        element={<PrivateRoute element={<AppreciationSuccess />} />}
      />

      {/* Admin-only routes */}
      <Route
        path="/analytics"
        element={<PrivateRoute element={<Analytics />} allowedRoles={['admin', 'admin']} />}
      />
      <Route
        path="/admin"
        element={<PrivateRoute element={<AdminDashboard />} allowedRoles={['admin', 'admin']} />}
      />
      <Route
        path="/admin/users"
        element={<PrivateRoute element={<AdminUserManagement />} allowedRoles={['admin', 'admin']} />}
      />
      <Route
        path="/admin/invitations"
        element={<PrivateRoute element={<AdminInvitations />} allowedRoles={['admin', 'admin']} />}
      />
      <Route
        path="/admin/settings"
        element={<PrivateRoute element={<AdminSettings />} allowedRoles={['admin', 'admin']} />}
      />
      <Route
        path="/admin/subscriptions"
        element={<PrivateRoute element={<AdminSubscriptions />} allowedRoles={['admin', 'admin']} />}
      />
      <Route
        path="/admin/subscriptions/checkout/:planId"
        element={<PrivateRoute element={<SubscriptionCheckout />} allowedRoles={['admin', 'admin']} />}
      />
    </Routes>
  );
};

const App = () => {
  const { currentUser } = useAppContext();
  const location = useLocation();

  // Note: Public pages (/, /login, /register, etc.) don't show header/footer
  // This is handled by conditional rendering in the routes below

  const shouldShowHeader = currentUser && !location.pathname.startsWith('/setup');
  const shouldShowFooter = currentUser && !location.pathname.startsWith('/setup');

  return (
    <div className="bg-gray-100 min-h-screen flex flex-col">
      {/* Only show normal header on authenticated pages */}
      {shouldShowHeader && <Header />}

      <div className="flex-grow">
        <AppRoutes />
      </div>

      {/* Only show normal footer on authenticated pages */}
      {shouldShowFooter && <Footer />}
    </div>
  );
};

const AppWrapper = () => {
  return (
    <Router>
      <App />
      <SpeedInsights />
    </Router>
  );
};

export default AppWrapper;